// src/Components/AssignPackageModal.js
import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Select from "react-select";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL } from "../../Config";
import "./AssignPackageModal.css"; // External styles for better customization

Modal.setAppElement("#root");

const AssignPackageModal = ({ isOpen, onRequestClose, userId }) => {
  const [agents, setAgents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [assignedPackageIds, setAssignedPackageIds] = useState([]);
  const [assignedPackageKeys, setAssignedPackageKeys] = useState([]);
  const [loadingAssignedPackages, setLoadingAssignedPackages] = useState(false);
  const [loading, setLoading] = useState(false);

  const getPackageId = (pkg) =>
    pkg?.packageid ?? pkg?.packageId ?? pkg?.packagesId ?? pkg?.id;

  const getPackageName = (pkg) =>
    pkg?.packageName ?? pkg?.PackageName ?? pkg?.name ?? "";

  const getPackagePrice = (pkg) => pkg?.packagePrice ?? pkg?.PackagePrice ?? "";

  const getPackageKey = (pkg) =>
    `${String(getPackageName(pkg)).trim().toLowerCase()}|${String(
      getPackagePrice(pkg)
    )
      .replace(/[^\d.]/g, "")
      .trim()}`;

  useEffect(() => {
    if (userId) {
      fetchAgents(userId);
      fetchPackages(userId);
    }
    setSelectedAgent(null);
    setSelectedPackages([]);
    setAssignedPackageIds([]);
    setAssignedPackageKeys([]);
  }, [userId]);

  const fetchAgents = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}Company_Agents?Userid=${userId}`
      );
      const agentOptions = response.data.agents.map((agent) => ({
        value: agent.agentId,
        label: agent.name,
      }));
      setAgents(agentOptions);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const fetchPackages = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}RetailCompanyPackage?id=${userId}`
      );
      const availablePackages = Array.isArray(response.data?.packages)
        ? response.data.packages
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];
      const packageOptions = availablePackages
        .map((pkg) => ({
          value: getPackageId(pkg),
          label: getPackageName(pkg),
          price: getPackagePrice(pkg),
          description:
            pkg?.packageDescription ??
            pkg?.PackageDescription ??
            pkg?.companyProduct ??
            pkg?.CompanyProduct ??
            "",
        }))
        .filter((pkg) => pkg.value != null && pkg.label);
      setPackages(packageOptions);
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  useEffect(() => {
    setSelectedPackages([]);

    if (!isOpen || !userId || !selectedAgent) {
      setAssignedPackageIds([]);
      setAssignedPackageKeys([]);
      return;
    }

    const fetchAssignedPackages = async () => {
      setLoadingAssignedPackages(true);
      try {
        const response = await axios.get(`${BASE_URL}AgentPackages?id=${userId}`, {
          headers: { accept: "*/*" },
        });
        const assignedRows = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const selectedAgentId = String(selectedAgent.value);
        const selectedAgentName = String(selectedAgent.label).toLowerCase();
        const agentIdFields = [
          "agentId",
          "agentID",
          "agentProfileId",
          "AgentProfileId",
          "salesAgentProfileId",
          "SalesAgentProfileId",
        ];
        const packageIdFields = [
          "packageid",
          "packageId",
          "PackageId",
          "packagesId",
          "PackagesId",
        ];
        const belongsToSelectedAgent = (row) =>
          agentIdFields.some((field) => String(row?.[field]) === selectedAgentId) ||
          ["agentName", "name"].some(
            (field) => String(row?.[field]).toLowerCase() === selectedAgentName
          );

        setAssignedPackageIds(
          assignedRows
            .filter(belongsToSelectedAgent)
            .map((row) => packageIdFields.map((field) => row?.[field]).find(Boolean))
            .filter((id) => id != null)
            .map(String)
        );
        setAssignedPackageKeys(
          assignedRows
            .filter(belongsToSelectedAgent)
            .map((row) => ({
              packageName:
                row?.packageName ??
                row?.PackageName ??
                row?.package_name ??
                row?.name,
              packagePrice: row?.packagePrice ?? row?.PackagePrice,
            }))
            .filter(Boolean)
            .map(getPackageKey)
        );
      } catch (error) {
        console.error("Error fetching assigned agent packages:", error);
        setAssignedPackageIds([]);
        setAssignedPackageKeys([]);
      } finally {
        setLoadingAssignedPackages(false);
      }
    };

    fetchAssignedPackages();
  }, [isOpen, userId, selectedAgent]);

  const assignedPackageKeyCounts = assignedPackageKeys.reduce((counts, key) => {
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

  const packageOptions = packages
    .filter((pkg) => {
      if (assignedPackageIds.includes(String(pkg.value))) return false;

      const key = getPackageKey({ packageName: pkg.label, packagePrice: pkg.price });
      if (!assignedPackageKeyCounts[key]) return true;

      assignedPackageKeyCounts[key] -= 1;
      return false;
    })
    .map((pkg) => ({
      ...pkg,
      label: `${pkg.label}${pkg.description ? ` (${pkg.description})` : ""}${
        (pkg.price ?? pkg.packagePrice) != null
          ? ` — Rs. ${pkg.price ?? pkg.packagePrice}`
          : ""
      }`,
    }));

  const handleAssignPackage = async () => {
    if (!selectedAgent || !selectedPackages.length) {
      Swal.fire(
        "Validation Error",
        "Please select an agent and at least one package.",
        "warning"
      );
      return;
    }

    setLoading(true);
    try {
      const responses = [];
      for (const selectedPackage of selectedPackages) {
        const response = await axios.post(
          `${BASE_URL}AssignPackage`,
          {
            salesAgentProfileId: selectedAgent.value,
            packagesId: selectedPackage.value,
          },
          {
            headers: { accept: "*/*", "Content-Type": "application/json" },
          }
        );
        responses.push(response.data);
      }

      const successfulCount = responses.filter(
        (response) => response?.responseCode === "0000"
      ).length;
      if (successfulCount === selectedPackages.length) {
        Swal.fire(
          "Success",
          `${successfulCount} package(s) assigned successfully.`,
          "success"
        );
        onRequestClose(); 
      } 
      else {
        Swal.fire(
          "Some Assignments Failed",
          `${successfulCount} of ${selectedPackages.length} package(s) were assigned.`,
          "error"
        );
      }
    } catch (error) {
      console.error("Error assigning package:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "An error occurred.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
   <Modal
  isOpen={isOpen}
  onRequestClose={onRequestClose}
  contentLabel="Assign Package Modal"
  style={{
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "600px",
      padding: "25px",
      borderRadius: "12px",
      backgroundColor: "#fff",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  }}
>
  <div className="assign-package-modal">
    <div className="modal-header">
      <h5 className="modal-title w-100 text-center">Assign Package to Agent</h5>
    </div>

    <div className="modal-body">
      <div className="form-group mb-3">
        <label className="form-label">Agent</label>
        <Select
          options={agents}
          value={selectedAgent}
          onChange={setSelectedAgent}
          isLoading={loading}
          placeholder="Select an Agent"
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      <div className="form-group mb-3">
        <label className="form-label">Package</label>
        <Select
          options={packageOptions}
          isMulti
          value={selectedPackages}
          onChange={setSelectedPackages}
          isLoading={loading || loadingAssignedPackages}
          isDisabled={!selectedAgent || loadingAssignedPackages}
          placeholder={
            loadingAssignedPackages ? "Loading assigned packages..." : "Select packages"
          }
          closeMenuOnSelect={false}
          noOptionsMessage={() => "No packages available"}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      <div className="modal-footer">
        <button
          className="btn btn-secondary"
          onClick={onRequestClose}
          disabled={loading}
        >
          Cancel
        </button>

        <button
          className="btn btn-primary"
          onClick={handleAssignPackage}
          disabled={loading}
        >
          {loading ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          ) : (
            "Assign Package"
          )}
        </button>
      </div>
    </div>
  </div>
</Modal>

  );
};

export default AssignPackageModal;
